import axios from 'axios';
import { adapters, MigrateOptions } from '@aison/migrator';

axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.withCredentials = true;
// Vite exposes env vars on import.meta.env (VITE_* prefix). In dev the
// default '/api' routes through the Vite proxy to the local migrator
// server; in production this should be set to the deployed API origin.
axios.defaults.baseURL = import.meta.env.VITE_API_URL || '/api';
axios.interceptors.response.use(
  response => response,
  error => {
    return Promise.reject(error?.response?.data || error);
  }
);

export interface ConnectRequest {
  type: adapters.InputAdapterType;
  adapter: adapters.AdapterOptions;
}

export interface QueryRequest extends ConnectRequest {
  read: { query: string }[];
}

export interface ReadRequest extends ConnectRequest {
  read: object;
}

export interface WriteRequest extends ConnectRequest {
  write: object;
}

type DatabaseAPI = {
  connect(object: ConnectRequest): Promise<boolean>;
  read(object: QueryRequest): Promise<object[]>;
};

export const database: DatabaseAPI = {
  async connect(options: ConnectRequest) {
    // console.log('connecting..', database);
    const res = await axios.post('/database/connect', options);
    // console.log('cnnnected!');
    return res.data.connected;
  },

  async read(options: QueryRequest) {
    const res = await axios.post('/database/query', options);
    return res.data;
  },
};

type AisonConnection = {
  sid?: string;
  token?: string;
  username?: string;
  password?: string;

  // Server
  url: string;
};

type AisonReadRequest = {
  appId?: string;
  datasets?: boolean;
};

export const aison = {
  async connect(options: ConnectRequest) {
    const res = await axios.post('/aison/connect', options);
    return res.data.connected;
  },
  async read(options: ReadRequest) {
    const res = await axios.post('/aison/apps', options);
    return res.data;
  },
  async write(options: WriteRequest) {
    const res = await axios.post('/aison/create-app', options);
    return res.data;
  },
};

// function onChunkedResponseComplete(result) {
//   // console.log('all done!', result);
// }

function processChunkedResponse(response, onData: any) {
  // console.log('process chunked response');
  // let item = 0;
  // const text = '';
  const reader = response.body.getReader();
  let partial;

  function appendChunks(result) {
    // console.log('item', item);
    // item++;
    if (!result.done) {
      const chunk = new Uint8Array(result.value.buffer);
      const string = new TextDecoder('utf-8').decode(chunk);
      const lines = string.split('\n').filter(v => v);
      // console.log('string value', string);
      // console.log('lines', lines);

      lines.forEach(line => {
        if (!line.endsWith('}')) {
          // We might get a partial json line back, in that case wait for next
          // chunk
          partial = line;
          return;
        } else if (partial) {
          line = partial + line;
          partial = undefined;
        }

        // Catch errors here so we don't fuck up response handling
        try {
          onData(line);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Error parsing chunked response', e);
          // console.log('offending line', line);
        }
      });
      // try {
      //   lines.map(onData);
      // } catch (e) {
      //   console.error('Error parsing chunked response', e);
      // }
    }

    if (result.done) {
      // console.log('returning');
      return 'done';
    } else {
      // console.log('recursing');
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return readChunk();
    }
  }

  function readChunk() {
    return reader.read().then(appendChunks);
  }

  return readChunk();
}

export const run = {
  async migrate(config: MigrateOptions, options: { onData?: any } = {}) {
    const { onData } = options;
    const res = fetch(axios.defaults.baseURL + '/run/migrate', {
      method: 'post',
      credentials: 'include',
      body: JSON.stringify({
        ...config,
        options: { log: { level: 'debug' }, stream: true },
      }),
      headers: {
        'content-type': 'application/json',
      },
    }).then(res => {
      return processChunkedResponse(res, onData);
    });
    // .catch(e => {
    // eslint-disable-next-line no-console
    //   console.error('run error!', e);
    // });
    // .then(onChunkedResponseComplete);
    // TODO: Check res is ok first?
    return res;
  },
};

export const tasks = {
  async getTasks() {
    const res = await axios.get('/tasks');
    return res.data;
  },

  async checkTask(taskId: string) {
    const res = await axios.get(`/tasks/${taskId}`);
    return res.data;
  },

  async deleteTask(taskId: string) {
    await axios.delete(`/tasks/${taskId}`);
  },

  async clearTasks() {
    await axios.post(`/tasks/clear`);
  },
};
